import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Frame, Monitor, Smartphone, TriangleAlert, X } from "lucide-react";
import { cn } from "lib/utils";

/* ══════════════════════════════════════════════════════════════════════════
   Live view of the template the assistant is building.

   Three ways to look at it, because they answer different questions:

     Normal   the email at its own width, no device around it. The default —
              the thing itself, before any opinion about where it is read.
     Desktop  the email sitting on a 1024px page, so the surrounding margin
              and background are visible the way an inbox shows them.
     Mobile   the email inside a 390px handset.

   Email HTML is a fixed-width table, 600–700px near enough always. Dropping
   that into a panel narrower than the table left the iframe's own document
   scrolling sideways, so the right edge was cut off and only reachable by
   dragging. Every mode is therefore measured and scaled to fit: the page
   scrolls one way only, and the zoom is stated rather than left to be
   inferred.

   `sandbox="allow-same-origin"` is what makes the measurement possible.
   Scripts stay blocked, so the document still cannot run anything, navigate
   the top frame, submit a form or reach the network — it can only be read.
   ══════════════════════════════════════════════════════════════════════════ */

type PreviewMode = "normal" | "desktop" | "mobile";

/** Logical viewport each mode renders at. 0 means "whatever the email is". */
const VIEWPORT: Record<PreviewMode, number> = { normal: 0, desktop: 1024, mobile: 390 };

/**
 * Chrome around the screen, in CSS px, taken out of the budget so the whole
 * frame fits: 1px borders on the bare card, bezels on the two devices.
 */
const CHROME_X: Record<PreviewMode, number> = { normal: 2, desktop: 16, mobile: 14 };
const CHROME_Y: Record<PreviewMode, number> = { normal: 0, desktop: 34, mobile: 62 };

/**
 * The handset's screen, in logical px.
 *
 * A phone has a size. Letting it grow to the full length of a 1,500px email
 * made a 1,500px-tall phone whose bottom bezel was somewhere below the panel —
 * it stopped looking like a phone and its chrome was unreachable. So the
 * handset keeps its shape and the email scrolls inside the screen, which is
 * what happens on the real one.
 *
 * The monitor is deliberately not in here. Its *width* is the thing being
 * simulated; its height is just how much of the email you can see at once, and
 * pinning that to a number left two thirds of the panel empty while the email
 * scrolled inside a letterbox. It takes whatever height is going instead.
 */
const PHONE_HEIGHT = 844;

/** Room kept under the device for the "wider than this screen" note. */
const NOTE_HEIGHT = 48;

const MODES = [
    { value: "normal", label: "Normal", icon: Frame },
    { value: "desktop", label: "Desktop", icon: Monitor },
    { value: "mobile", label: "Mobile", icon: Smartphone },
] as const;

/** Width to render at before the document has reported its own. */
const FALLBACK_WIDTH = 680;
/** Height to reserve before the document has reported its own. */
const ASSUMED_HEIGHT = 760;

export function TemplatePreviewPanel({
    html,
    receivedAtMs,
    isStreaming,
    onClose,
}: {
    html: string;
    receivedAtMs: number;
    isStreaming: boolean;
    onClose: () => void;
}) {
    const [mode, setMode] = useState<PreviewMode>("normal");
    const bezel = CHROME_X[mode];

    const scrollRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [available, setAvailable] = useState(0);
    const [availableHeight, setAvailableHeight] = useState(0);
    const [content, setContent] = useState<{ width: number; height: number; background: string } | null>(null);

    /* A new version arrived. The flash is what tells the user their last message
       actually changed something — the two renders can look near-identical. */
    const [justUpdated, setJustUpdated] = useState(false);
    const firstVersionRef = useRef(receivedAtMs);
    useEffect(() => {
        if (receivedAtMs === firstVersionRef.current) return;
        setJustUpdated(true);
        const timer = setTimeout(() => setJustUpdated(false), 1200);
        return () => clearTimeout(timer);
    }, [receivedAtMs]);

    /* How much room the panel actually has. Re-measured because this panel sits
       in a split the user can drag. */
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        setAvailable(el.clientWidth);
        setAvailableHeight(el.clientHeight);
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(([entry]) => {
            const width = Math.round(entry.contentRect.width);
            const height = Math.round(entry.contentRect.height);
            setAvailable((prev) => (prev === width ? prev : width));
            setAvailableHeight((prev) => (prev === height ? prev : height));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    /* Measurement resets with the document: a regenerated template is a
       different size, and keeping the old one would scale it wrongly. */
    useEffect(() => setContent(null), [receivedAtMs, mode]);

    /* A new device is a new layout — half-way down the old one is nowhere in
       particular on the new one. */
    useEffect(() => {
        // `scrollTop`, not `scrollTo` — the latter is a method jsdom does not
        // implement, so it takes the whole panel down under test.
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [mode, receivedAtMs]);

    /* Which document is actually on screen. Switching device remounts the
       frame — see the `key` below — so this changes and the document is
       re-measured at the new viewport. Without the remount the measurement
       taken at 680px survived the switch to a 390px handset, the frame was
       built for a 390px document, and the template was cut off on the right.

       It is a key rather than a counter because measuring must not happen
       between the switch and the new document arriving: the outgoing document
       would be measured at the outgoing viewport, and since that measurement
       then sets the width the incoming one renders at, it confirmed itself and
       stuck — 1024px desktop numbers survived all the way back into Normal. */
    const frameKey = `${receivedAtMs}:${mode}`;
    /* A counter as well as the key. Browsers fire `load` for the initial
       about:blank before the srcdoc arrives, and the blank guard in `measure`
       correctly ignores it — but with only a key to depend on, the real load
       set the same value, React saw no change, and the effect never re-ran.
       Nothing was ever measured, and every number on screen was the fallback
       that happened to match what was expected. */
    const [loaded, setLoaded] = useState<{ key: string; n: number }>({ key: "", n: 0 });

    /* Whether the device's screen is scrolled to the bottom, so the hint that
       there is more below can be taken away once there is not. */
    const screenRef = useRef<HTMLDivElement>(null);
    const [atEnd, setAtEnd] = useState(false);
    useEffect(() => setAtEnd(false), [receivedAtMs, mode]);

    const measure = useCallback(() => {
        const doc = frameRef.current?.contentDocument;
        const root = doc?.documentElement;
        if (!root || !doc?.body) return;
        // A blank document reports a height of 1, which would collapse the
        // frame. Browsers fire `load` for the initial about:blank as well.
        if (!doc.body.childElementCount && !doc.body.textContent?.trim()) return;
        // A table wider than the viewport still has to fit, so the content's
        // own width wins whenever it is the larger of the two.
        const width = Math.max(root.scrollWidth, doc.body.scrollWidth, VIEWPORT[mode] || 1);
        /* Body only. `documentElement.scrollHeight` is at least the viewport,
           so once the frame is taller than the email it reports the frame's own
           height back — the measurement would latch onto whatever height it had
           just been given and the tail of a longer template stayed clipped. */
        const style = doc.defaultView?.getComputedStyle(doc.body);
        const margins =
            (parseFloat(style?.marginTop || "0") || 0) + (parseFloat(style?.marginBottom || "0") || 0);
        const height = Math.max(Math.ceil(doc.body.scrollHeight + margins), 1);
        /* A short email leaves the rest of the handset empty. Filling that with
           the email's own page colour reads as "the message ends here"; filling
           it with white reads as "something failed to load".
        
           Reading `body` alone is not enough: email HTML usually leaves the body
           white and paints the page colour on a wrapper table inside it, which
           left a white band under a cream email. So this asks what is actually
           painted at the foot of the document and walks up from there. */
        const opaque = (el: Element | null): string | null => {
            for (let node = el; node && node !== doc.documentElement; node = node.parentElement) {
                const colour = doc.defaultView?.getComputedStyle(node).backgroundColor || "";
                if (colour && colour !== "transparent" && !/rgba\(0,\s*0,\s*0,\s*0\)/.test(colour)) return colour;
            }
            return null;
        };
        const background =
            opaque(doc.elementFromPoint(Math.min(width, doc.documentElement.clientWidth) / 2, height - 2)) ??
            opaque(doc.body) ??
            "#ffffff";
        // Same numbers must not produce a new object: re-rendering resizes the
        // frame, which is what feeds the observer below.
        /* Width only ever grows while a document is on screen. Rendering at the
           measured width is what produces the next measurement, so a template
           that reports one width at 680px and a different one at 700px would
           swap between them forever. `content` is reset for every new document
           and every device change, so nothing stale is carried across. */
        setContent((prev) => {
            const next = { width: Math.max(prev?.width ?? 0, width), height, background };
            return prev && prev.width === next.width && prev.height === next.height && prev.background === next.background
                ? prev
                : next;
        });
    }, [mode]);

    /* Images arrive after `load` and change the height, so the document is
       watched rather than measured once. */
    useEffect(() => {
        if (loaded.key !== frameKey) return; // the current document is not up yet
        const doc = frameRef.current?.contentDocument;
        if (!doc?.documentElement || !doc.body) return;
        measure();
        if (typeof ResizeObserver === "undefined") return;
        let pending = 0;
        /* Measuring resizes the very element being observed. Doing that inside
           the callback is the loop the browser reports as "ResizeObserver loop
           completed with undelivered notifications"; deferring to the next
           frame ends the delivery first and breaks the cycle. */
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(pending);
            pending = requestAnimationFrame(measure);
        });
        /* The body, not the root: the root is stretched to the frame, so its
           box never changes and late-loading images would go unnoticed. */
        observer.observe(doc.body);
        observer.observe(doc.documentElement);
        return () => {
            cancelAnimationFrame(pending);
            observer.disconnect();
        };
    }, [loaded, frameKey, measure]);

    const contentWidth = content?.width ?? (VIEWPORT[mode] || FALLBACK_WIDTH);
    const contentHeight = content?.height ?? ASSUMED_HEIGHT;

    /*
     * Two steps, because the device has to keep its own shape.
     *
     * The document is rendered at its own measured width, so a responsive email
     * that reflows at 390px measures 390 and fills the handset honestly, while a
     * fixed 680px table measures 680 and gets zoomed down into it — which is
     * what a real mail client does with one. `inner` is that zoom; `outer` then
     * fits the whole device into whatever room the panel has.
     */
    const deviceWidth = VIEWPORT[mode] || contentWidth;
    const inner = Math.min(1, deviceWidth / contentWidth);

    /*
     * The email is wider than the screen it is being shown on, so it is zoomed
     * out to fit — which is exactly what the recipient's mail client does.
     *
     * That is the most useful thing a device preview can tell someone sending
     * marketing email, and it was left to be inferred from the text having gone
     * small. `inner < 1` is the measurement itself: the document's own width did
     * not fit the viewport.
     */
    const tooWide = mode !== "normal" && content != null && inner < 0.995;
    /* Only ever shrinks. Blowing a 680px email up to fill a wide panel would
       show it at a size no recipient will ever see it at. */
    const budget = Math.max(0, available - bezel);
    /* The handset also has to fit top to bottom, or its lower bezel ends up
       below the panel — which is how it lost its home indicator. */
    /* The note sits under the device and is the whole point of showing it, so
       the device gives up the room rather than pushing it off the panel. */
    const heightBudget = Math.max(0, availableHeight - CHROME_Y[mode] - (tooWide ? NOTE_HEIGHT : 0));
    const fits = [budget > 0 ? budget / deviceWidth : 1];
    if (mode === "mobile" && heightBudget > 0) fits.push(heightBudget / PHONE_HEIGHT);
    const outer = Math.min(1, ...fits);
    const scale = inner * outer;
    const zoom = Math.round(scale * 100);
    const screenWidth = deviceWidth * outer;
    /* The email's full length, drawn inside the screen. */
    const pageHeight = contentHeight * scale;
    /*
     * What the device shows at once.
     *
     *   normal   everything — no device, so the panel does the scrolling
     *   mobile   the handset's screen, kept to its own proportions
     *   desktop  the room that is going, never more than the email needs, so a
     *            long one fills the panel and a short one is not padded out
     */
    const screenHeight =
        mode === "normal"
            ? pageHeight
            : mode === "mobile"
              ? PHONE_HEIGHT * outer
              : Math.min(heightBudget, pageHeight);
    /* Only the two devices clip and scroll their contents. */
    const scrolls = mode !== "normal" && screenHeight < pageHeight - 0.5;
    /* Enough room to spell the device names out next to their icons. */
    const roomy = available >= 430;

    const screen = (
        <div className="relative" style={{ width: screenWidth, height: screenHeight }}>
            <div
                ref={screenRef}
                onScroll={(event) => {
                    const el = event.currentTarget;
                    const done = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
                    setAtEnd((prev) => (prev === done ? prev : done));
                }}
                /* A browser scrollbar running down the inside of a phone breaks
                   the illusion and puts a grey stripe on every preview. The bar
                   is hidden; the fade below carries the same message. */
                className={cn("h-full w-full", scrolls ? "no-scrollbar overflow-y-auto overflow-x-hidden" : "overflow-hidden")}
                style={{ background: content?.background ?? "#ffffff" }}
            >
                {/* Sizer at the *visual* height. A transform does not change
                    layout, so without this the scroll range would be the
                    unscaled length. */}
                <div className="overflow-hidden" style={{ width: screenWidth, height: pageHeight }}>
            <div
                style={{
                    width: contentWidth,
                    height: contentHeight,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                }}
            >
                {/* Keyed on the version *and* the device so a regenerated
                    template — or a switch of viewport — reloads the document
                    instead of the browser diffing srcDoc in place. */}
                <iframe
                    ref={frameRef}
                    key={frameKey}
                    title="Generated template preview"
                    srcDoc={html}
                    sandbox="allow-same-origin"
                    onLoad={() => setLoaded((prev) => ({ key: frameKey, n: prev.n + 1 }))}
                    scrolling="no"
                    style={{ width: contentWidth, height: contentHeight }}
                    className="block border-none bg-white"
                />
                </div>
                </div>
            </div>

            {/* There is more of the email below this edge. It goes away on
                arrival at the bottom, so it never claims more than there is. */}
            {scrolls && !atEnd && (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/20 to-transparent"
                />
            )}
        </div>
    );

    return (
        <aside
            className="flex h-full min-h-0 w-full flex-col border-l border-line bg-surface-muted dark:border-dark-800 dark:bg-dark-900"
            aria-label="Template preview"
        >
            <header className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-3 py-2.5 dark:border-dark-800">
                <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-ui font-semibold text-content dark:text-slate-100">Preview</span>
                    {(isStreaming || justUpdated) && (
                        <span
                            className={cn(
                                "rounded-full px-2 py-0.5 text-micro font-semibold",
                                isStreaming
                                    ? "bg-accent-muted text-accent dark:bg-dark-700"
                                    : "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300",
                            )}
                            role="status"
                            aria-live="polite"
                        >
                            {isStreaming ? "Updating…" : "Updated"}
                        </span>
                    )}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                    {/* What is actually on screen: the width being simulated,
                        and whether it had to be shrunk to get there. */}
                    <span
                        className="mr-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-micro font-semibold tabular-nums text-content-muted dark:text-slate-400"
                        title={
                            zoom < 100
                                ? `Previewing at ${Math.round(deviceWidth)}px, scaled to ${zoom}% to fit`
                                : `Previewing at ${Math.round(deviceWidth)}px`
                        }
                    >
                        {Math.round(deviceWidth)}px{zoom < 100 ? ` · ${zoom}%` : ""}
                    </span>

                    <div className="mr-1 flex items-center gap-0.5 rounded-lg bg-surface p-0.5 dark:bg-dark-800">
                        {MODES.map(({ value, label, icon: Icon }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setMode(value)}
                                aria-label={`${label} preview`}
                                aria-pressed={mode === value}
                                className={cn(
                                    "flex items-center gap-1.5 rounded-md px-1.5 py-1.5 font-semibold transition-colors focus-ring",
                                    mode === value
                                        ? "bg-accent-muted text-accent dark:bg-dark-700"
                                        : "text-content-muted hover:text-content dark:text-slate-400 dark:hover:text-slate-200",
                                )}
                            >
                                <Icon className="h-4 w-4" />
                                {/* Named once the panel is wide enough to say it — the
                                    split is draggable, so this follows the measured
                                    width rather than the viewport's. */}
                                {roomy && <span className="text-micro">{label}</span>}
                            </button>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close preview"
                        className="rounded-lg p-1.5 text-content-muted transition-colors hover:text-content focus-ring dark:text-slate-400 dark:hover:text-slate-200"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </header>

            {/* One axis only: the frame is scaled to the panel, so nothing ever
                needs dragging sideways to be read. */}
            <div
                ref={scrollRef}
                /* `scrollbar-gutter: stable` keeps the gutter reserved whether or
                   not a bar is showing. Without it the panel's usable width
                   changes the moment the frame grows tall enough to scroll,
                   which rescales the frame, which can make it short enough not
                   to scroll — the loop that shows up as a preview twitching in
                   place. Only Normal can reach it: the two devices are fitted
                   to the panel and never make it scroll. */
                style={{ scrollbarGutter: "stable" }}
                className="scrollbar-slim min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6"
            >
                {mode === "normal" ? (
                    /* No device: the email at its own width, which is what the
                       reader is actually composing. */
                    <div
                        className={cn(
                            "mx-auto overflow-hidden rounded-xl border border-line bg-white shadow-xl transition-shadow duration-300 dark:border-dark-700",
                            justUpdated && "ring-2 ring-accent/60",
                        )}
                        style={{ width: screenWidth + bezel }}
                    >
                        {screen}
                    </div>
                ) : mode === "desktop" ? (
                    /* A monitor, not a browser window. The macOS traffic lights
                       that were here read as "web page"; an email is not one,
                       and three fake buttons cost 30px to say nothing. A bezel
                       says "this is a screen" and matches the handset below, so
                       the two modes belong to one family instead of two. */
                    <div
                        className={cn(
                            "mx-auto rounded-[1.25rem] bg-slate-900 p-2 shadow-2xl transition-shadow duration-300 dark:bg-dark-800",
                            justUpdated && "ring-2 ring-accent/60",
                        )}
                        style={{ width: screenWidth + bezel }}
                    >
                        <div className="overflow-hidden rounded-[0.6rem] bg-white">{screen}</div>
                        <div className="flex h-[18px] items-center justify-center">
                            <span className="h-[3px] w-[54px] rounded-full bg-slate-700 dark:bg-dark-600" />
                        </div>
                    </div>
                ) : (
                    <div
                        className={cn(
                            "mx-auto rounded-[2.25rem] bg-slate-900 p-[7px] shadow-2xl transition-shadow duration-300 dark:bg-dark-800",
                            justUpdated && "ring-2 ring-accent/60",
                        )}
                        style={{ width: screenWidth + bezel }}
                    >
                        <div className="overflow-hidden rounded-[1.75rem] bg-slate-900 dark:bg-dark-800">
                            {/* Earpiece slot and home indicator, so the screen is a
                                screen rather than a rounded rectangle. Both sit
                                beside the content instead of over it — a notch
                                overlay would hide the top of the email. */}
                            <div className="flex h-[26px] items-center justify-center">
                                <span className="h-[5px] w-[46px] rounded-full bg-slate-700 dark:bg-dark-600" />
                            </div>
                            {screen}
                            <div className="flex h-[22px] items-center justify-center">
                                <span className="h-[4px] w-[96px] rounded-full bg-slate-600 dark:bg-dark-500" />
                            </div>
                        </div>
                    </div>
                )}

                {tooWide && (
                    <p className="mx-auto mt-3 flex max-w-sm items-start justify-center gap-1.5 text-center text-meta text-content-muted">
                        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
                        <span>
                            This email is {Math.round(contentWidth)}px wide — wider than a{" "}
                            {Math.round(deviceWidth)}px screen, so it arrives zoomed out.
                        </span>
                    </p>
                )}
            </div>
        </aside>
    );
}

export default TemplatePreviewPanel;
